import { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { evalApiClient } from '@/lib/api/eval.client'
import { EvalPromptType, EVAL_PROMPT_TYPE_LABELS } from '@super/shared-types'
import type { EvalCase } from '@super/shared-types'
import {
  EVAL_PROMPT_TEMPLATES,
  deriveEvalCaseFields,
  type EvalCaseFields,
} from '@super/eval-framework'
import {
  AssertionBuilder,
  serializeAssertions,
  hasCompleteAssertion,
  type AssertionRow,
} from './AssertionBuilder'

interface Props {
  open: boolean
  onClose: () => void
  onCreated: () => void
  initialCase?: EvalCase
  companyId?: string
  onSuccess?: (msg: string) => void
  onError?: (msg: string) => void
}

function getFieldsForPromptType(promptType: EvalPromptType): EvalCaseFields {
  const template = EVAL_PROMPT_TEMPLATES[promptType]
  if (!template) return { inputFields: [], outputFields: [] }
  return deriveEvalCaseFields(template)
}

function getTemplateMetaForPromptType(promptType: EvalPromptType) {
  const template = EVAL_PROMPT_TEMPLATES[promptType]
  return {
    model: template?.metadata?.model ?? null,
    version: template?.version ?? null,
  }
}

function emptyAssertionRow(): AssertionRow {
  return { id: crypto.randomUUID(), field: '', operator: '', value: '' }
}

function initFromCase(c: EvalCase): {
  values: Record<string, string>
  assertions: AssertionRow[]
} {
  const fields = getFieldsForPromptType(c.promptType as EvalPromptType)
  const inputCtx = (c.inputContext as Record<string, unknown>) ?? {}
  const expectedOut = (c.expectedOutput ?? {}) as Record<string, unknown>
  const values: Record<string, string> = {}

  for (const field of fields.inputFields) {
    if (field.target === 'content') {
      values[field.key] = c.content
    } else if (field.type === 'json-array') {
      values[field.key] =
        inputCtx[field.key] != null
          ? JSON.stringify(inputCtx[field.key], null, 2)
          : ''
    } else if (field.type === 'line-list') {
      const arr = inputCtx[field.key]
      values[field.key] = Array.isArray(arr) ? arr.join('\n') : ''
    } else {
      values[field.key] =
        inputCtx[field.key] != null ? String(inputCtx[field.key]) : ''
    }
  }

  const assertions: AssertionRow[] = []
  for (const [key, spec] of Object.entries(expectedOut)) {
    const s = spec as Record<string, unknown>
    const op = Object.keys(s).find(k => k.startsWith('$')) ?? '$exact'
    const value =
      op === '$empty'
        ? ''
        : op === '$in'
          ? (s.$in as unknown[]).map(x => String(x)).join(', ')
          : String(s[op] ?? '')
    assertions.push({
      id: crypto.randomUUID(),
      field: key,
      operator: op,
      value,
    })
  }

  return {
    values,
    assertions: assertions.length > 0 ? assertions : [emptyAssertionRow()],
  }
}

export function AddEvalCaseModal({
  open,
  onClose,
  onCreated,
  initialCase,
  companyId,
  onSuccess,
  onError,
}: Props) {
  const isEditMode = !!initialCase

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [promptType, setPromptType] = useState<EvalPromptType>(
    EvalPromptType.PROPERTY_MATCHING,
  )
  const [values, setValues] = useState<Record<string, string>>({})
  const [assertions, setAssertions] = useState<AssertionRow[]>([
    emptyAssertionRow(),
  ])
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const skipResetRef = useRef(false)
  const fields = getFieldsForPromptType(promptType)

  const templateMeta =
    isEditMode && initialCase
      ? {
          model: initialCase.model ?? null,
          version: initialCase.promptVersion ?? null,
        }
      : getTemplateMetaForPromptType(promptType)

  useEffect(() => {
    if (open && initialCase) {
      skipResetRef.current = true
      setName(initialCase.name)
      setDescription(initialCase.description ?? '')
      setPromptType(initialCase.promptType as EvalPromptType)
      const { values: v, assertions: a } = initFromCase(initialCase)
      setValues(v)
      setAssertions(a)
      setError(null)
    }
  }, [open, initialCase?.id])

  useEffect(() => {
    if (skipResetRef.current) {
      skipResetRef.current = false
      return
    }
    setValues({})
    setAssertions([emptyAssertionRow()])
    setError(null)
  }, [promptType])

  function setValue(key: string, v: string) {
    setValues(prev => ({ ...prev, [key]: v }))
  }

  function reset() {
    setName('')
    setDescription('')
    setPromptType(EvalPromptType.PROPERTY_MATCHING)
    setValues({})
    setAssertions([emptyAssertionRow()])
    setError(null)
  }

  async function handleSubmit() {
    setError(null)

    let content = ''
    const inputContext: Record<string, unknown> = {}

    for (const field of fields.inputFields) {
      const raw = values[field.key] ?? ''
      if (field.required && !raw.trim()) {
        setError(`${field.label} is required`)
        return
      }
      if (!raw.trim()) continue

      if (field.target === 'content') {
        content = raw
      } else if (field.type === 'json-array') {
        try {
          inputContext[field.key] = JSON.parse(raw)
        } catch {
          setError(`${field.label} must be valid JSON`)
          return
        }
      } else if (field.type === 'line-list') {
        inputContext[field.key] = raw
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean)
      } else {
        inputContext[field.key] = raw
      }
    }

    const expectedOutput = serializeAssertions(assertions, fields.outputFields)
    if (Object.keys(expectedOutput).length === 0) {
      setError('At least one assertion is required')
      return
    }

    setIsSaving(true)

    if (isEditMode && initialCase) {
      const result = await evalApiClient.updateCase(initialCase.id, {
        name,
        description: description || undefined,
        content,
        inputContext:
          Object.keys(inputContext).length > 0 ? inputContext : null,
        expectedOutput,
      })
      setIsSaving(false)
      if (result) {
        onSuccess?.('Case updated')
        reset()
        onCreated()
        onClose()
      } else {
        onError?.('Failed to update case')
        setError('Failed to update eval case')
      }
    } else {
      const result = await evalApiClient.createCase({
        name,
        description: description || undefined,
        companyId,
        promptType,
        content,
        inputContext:
          Object.keys(inputContext).length > 0 ? inputContext : undefined,
        expectedOutput,
      })
      setIsSaving(false)
      if (result) {
        onSuccess?.('Case created')
        reset()
        onCreated()
        onClose()
      } else {
        onError?.('Failed to create case')
        setError('Failed to create eval case')
      }
    }
  }

  const isFormValid =
    (name ?? '').trim().length > 0 &&
    fields.inputFields
      .filter(f => f.required)
      .every(f => (values[f.key] ?? '').trim().length > 0) &&
    hasCompleteAssertion(assertions)

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) {
          reset()
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Eval Case' : 'Add Eval Case'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {/* ── Meta ── */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ec-name">Name *</Label>
              <Input
                id="ec-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Roosevelt Blvd exact match"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Prompt type *</Label>
              <div className="flex items-center gap-2">
                <Select
                  value={promptType}
                  onValueChange={v => setPromptType(v as EvalPromptType)}
                  disabled={isEditMode}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(EvalPromptType).map(pt => (
                      <SelectItem key={pt} value={pt}>
                        {EVAL_PROMPT_TYPE_LABELS[pt]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(templateMeta.model || templateMeta.version) && (
                  <div className="flex flex-col gap-0.5 min-w-0 max-w-[120px]">
                    {templateMeta.version && (
                      <div className="text-xs text-muted-foreground font-mono truncate">
                        v{templateMeta.version}
                      </div>
                    )}
                    {templateMeta.model && (
                      <div className="text-xs text-muted-foreground font-mono truncate">
                        {templateMeta.model}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ec-description">Description</Label>
            <Input
              id="ec-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          {/* ── Input fields ── */}
          {fields.inputFields.map(field => (
            <div key={field.key} className="flex flex-col gap-1.5">
              <Label htmlFor={`ec-in-${field.key}`}>
                {field.label}
                {field.required ? ' *' : ''}
              </Label>
              <Textarea
                id={`ec-in-${field.key}`}
                value={values[field.key] ?? ''}
                onChange={e => setValue(field.key, e.target.value)}
                rows={field.rows ?? 3}
                className={
                  field.type === 'json-array' ? 'font-mono text-xs' : undefined
                }
              />
            </div>
          ))}

          {/* ── Assertions ── */}
          {fields.outputFields.length > 0 && (
            <div className="border-t pt-4 flex flex-col gap-3">
              <p className="text-sm font-medium text-muted-foreground">
                Assertions
              </p>
              <AssertionBuilder
                fields={fields.outputFields}
                value={assertions}
                onChange={setAssertions}
              />
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !isFormValid}>
            {isSaving ? 'Saving…' : isEditMode ? 'Save changes' : 'Create case'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
